import {
  ApprovalTerms,
  ApprovalBadge,
  ComparisonRowConfig,
  LenderApproval,
  GqlLenderFee,
  LenderFeeType,
  Condition,
} from '../types';
import { formatCurrency } from '../../../utils';
import { resolveLatestProposal } from 'modules/creditSubmission';
import { CreditSubmissionDecisionFee, CreditSubmissionDecisionViewModel } from '@trnsact/trnsact-shared-types';
import { CreditSubmission } from '@trnsact/trnsact-shared-types/src/generated';

type TermKey = keyof Omit<ApprovalTerms, 'conditions' | 'lenderFees'>;

/**
 * Determines which lenders get "best" and "worst" badges for a given row.
 * The `favorable` flag indicates whether the value is the best (true) or worst (false) for that metric.
 */
export function computeBadges(approvals: LenderApproval[], config: ComparisonRowConfig): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (!config.bestIs || approvals.length < 2) return result;

  const key = config.key as TermKey;

  const numericValues = approvals
    .map(a => {
      const raw = a.approvalTerms[key];
      const value = typeof raw === 'number' ? raw : null;
      return { id: a.id, value };
    })
    .filter((entry): entry is { id: string; value: number } => entry.value !== null);

  if (numericValues.length < 2) return result;

  const sorted = [...numericValues].sort((a, b) => a.value - b.value);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.value === maxEntry.value) return result;

  if (config.bestIs === 'highest') {
    result[maxEntry.id] = { label: 'highest', favorable: true };
    result[minEntry.id] = { label: 'lowest', favorable: false };
  } else {
    result[minEntry.id] = { label: 'lowest', favorable: true };
    result[maxEntry.id] = { label: 'highest', favorable: false };
  }

  return result;
}

/**
 * Computes badges specifically for the conditions count row.
 * Fewer conditions = favorable.
 */
export function computeConditionsBadges(approvals: LenderApproval[]): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (approvals.length < 2) return result;

  const entries = approvals.map(a => ({
    id: a.id,
    count: a.approvalTerms.conditions.length,
  }));

  const sorted = [...entries].sort((a, b) => a.count - b.count);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.count === maxEntry.count) return result;

  result[minEntry.id] = { label: 'fewest', favorable: true };
  result[maxEntry.id] = { label: 'most', favorable: false };

  return result;
}

// ---------------------------------------------------------------------------
// API → UI mapper
// ---------------------------------------------------------------------------

const APPROVED_LABELS = new Set(['Approved', 'Pre-Approved']);
// Raw Dynamics creditSubStage numeric codes that represent approvals
const APPROVED_STAGE_CODES = new Set(['804790003', '804790013']);

/**
 * Returns true when a credit submission represents an approval.
 * Checks both the human-readable decision field and the raw Dynamics stage code.
 */
export function isApproved(cs: { stage: string | null | undefined; decision: string | null | undefined }): boolean {
  return APPROVED_LABELS.has(cs.decision ?? '') || APPROVED_STAGE_CODES.has(cs.stage ?? '');
}

/**
 * Splits a contingencies string (plain text) into structured Condition objects.
 * Handles numbered lists, bullet points, semicolons, and plain newlines.
 */
export function parseContingencies(contingencies: Array<string | null | undefined> | null | undefined): Condition[] {
  if (!contingencies) return [];
  return contingencies
    .filter((s): s is string => s != null)
    .map(s => s.replace(/^\d+\.\s*/, '').trim())
    .filter(Boolean)
    .map((description, index) => ({ id: `cond-${index}`, description }));
}

/**
 * Maps a raw GqlCreditSubmissionApproval from the API to the internal LenderApproval shape.
 *
 * When AI-parsed data is available it takes precedence, otherwise the mapper
 * falls back to the traditional credit-submission fields and proposals.
 */
export function mapCreditSubmissionToApproval(cs: CreditSubmission): LenderApproval {
  const parsed = cs.creditDecisionParsedData?.parsedData;

  // --- When we have AI-parsed data, prefer it --------------------------
  if (parsed) {
    const conditions: Condition[] =
      parsed.conditions && parsed.conditions.length > 0
        ? parsed.conditions
            .filter((d): d is string => d != null)
            .map((description, index) => ({ id: `cond-${index}`, description }))
        : [];

    return {
      id: cs.creditSubmissionId ?? '',
      lenderName: cs.lenderName ?? 'Unknown Lender',
      parsedDecisionId: cs.creditDecisionParsedData!.parsedDecisionId ?? null,
      approvalTerms: {
        conditions,
        interestRate: parsed.interestRate ?? null,
        approvedAmount: parsed.approvalAmount ?? null,
        monthlyPayment: parsed.monthlyPayment ?? null,
        termLengthMonths: parsed.termLengthMonths ?? null,
        daysToFirstPayment: parsed.daysToFirstPayment ?? null,
        downPaymentToVendor: parsed.downPaymentToVendor ?? null,
        minimumRequiredDownPayment: parsed.minimumRequiredDownPayment?.toString() ?? null,
        lenderFees: (parsed.lenderFees ?? []).filter((f): f is GqlLenderFee => f != null),
      },
    };
  }

  // --- Fallback: build from traditional CS fields + decision -----------
  const conditions = parseContingencies(cs.contingencies);
  const decision = cs.decisions?.[0] as CreditSubmissionDecisionViewModel;

  return {
    parsedDecisionId: null,
    id: cs.creditSubmissionId ?? '',
    lenderName: cs.lenderName ?? 'Unknown Lender',
    approvalTerms: {
      conditions,
      approvedAmount: decision?.maxApprovalAmount ?? cs.approvedAmount ?? null,
      termLengthMonths: decision?.termMonths
        ? decision?.termMonths
        : decision?.termRates?.map(tr => tr.term)?.join(', '),
      daysToFirstPayment: decision?.daysToFirstPayment ?? null,
      interestRate: decision?.interestRate /* ?? cs.approvedRate */ ?? null,
      monthlyPayment: null,
      lenderFees:
        decision?.fees?.map((feeItem: CreditSubmissionDecisionFee) => ({
          amount: feeItem.fee,
          description: feeItem.identifier,
          canBeFinanced: feeItem.financed,
          feeType: (feeItem.type as LenderFeeType) ?? null,
        })) ?? [],
      downPaymentToVendor: null,
      minimumRequiredDownPayment: decision?.minDownPayment?.toString() ?? null,
      numAdvancePayments: decision?.numAdvancePayments ?? null,
    },
  };
}

/**
 * Formats a cell value based on its configured format type.
 */
export function formatCellValue(
  value: string | number | null | undefined,
  format: ComparisonRowConfig['format']
): string {
  if (value === null || value === undefined) return '—';

  switch (format) {
    case 'currency': {
      const num = Number(value);
      return isNaN(num) ? String(value) : `$${num.toLocaleString('en-US')}`;
    }

    case 'percentage':
      return `${value}%`;

    case 'months': {
      const num = Number(value);
      return isNaN(num) ? String(value) : `${num} mo`;
    }

    case 'number':
      return String(value);
    case 'text':

    default:
      return String(value);
  }
}
